import type { LinkProps } from '@ps/web-shared-components';
import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { select } from '@swf/core/store';
import { generateSportsPath } from '@swf/sports/utils/generateSportsPath';

import { ScrollableList, Tab } from '../../design-system/components';
import { SportsLink } from '../SportsLink/SportsLink';

const getActiveProp = (
  isActive: (() => boolean) | undefined,
  pathname: string,
  fullRoute: string | undefined
) => {
  const activeProp = {
    active: true,
    'aria-current': 'page'
  };

  if (isActive) {
    return isActive() ? activeProp : {};
  }

  return pathname === fullRoute ? activeProp : {};
};

export interface TabLinkProps extends LinkProps {
  fullRoute?: string | undefined;
  isActive?: (() => boolean) | undefined;
}

const TabLink: React.FC<TabLinkProps> = ({ fullRoute, ...restProps }) => (
  <Tab as={SportsLink} role="link" {...restProps} />
);

const TabLinkMenu: React.FC<{
  children: (React.ReactElement<TabLinkProps> | null)[];
}> & { Link: typeof TabLink } = ({ children }) => {
  const location = useLocation();
  const aliases = useSelector(select.cms.getAliases);
  const tabMenuContainerRef = useRef<HTMLDivElement>(null);

  if (!children.length) return null;

  return (
    <ScrollableList ref={tabMenuContainerRef} as="nav" role="navigation">
      {React.Children.map(children, (child) => {
        if (!child || !React.isValidElement(child)) return null;
        const { alias, isActive } = child.props;
        const aliasEntry = aliases[alias as keyof typeof aliases];

        const fullRoute =
          aliasEntry &&
          generateSportsPath(
            aliasEntry,
            child.props.values as Record<string, string | number | boolean | undefined>
          );

        return React.cloneElement(child, {
          fullRoute,
          ...getActiveProp(isActive, location.pathname, fullRoute),
          ...child.props
        });
      })}
    </ScrollableList>
  );
};

TabLinkMenu.Link = TabLink;
export { TabLinkMenu };
